import { useCallback, useMemo, useRef, useState } from 'react';
import { Button, TextArea } from '@heroui/react';

interface ComposerProps {
  /** Hard-disable the composer (e.g. read-only context). */
  disabled: boolean;
  /** A turn is mid-stream — swap Send → Stop. */
  isStreaming: boolean;
  /** Stop has been clicked but the server hasn't acknowledged yet
   *  (often blocked by an in-flight long tool call). The Stop button
   *  becomes a disabled "Stopping…" affordance so the user knows the
   *  click registered. */
  isStopping?: boolean;
  /** WS is closed/closing; partysocket will auto-reconnect. We keep the
   *  composer ENABLED (useAgentChat queues + replays on open) but show a
   *  hint so the user understands why their message may sit briefly. */
  isReconnecting?: boolean;
  /**
   * Send a message. AI SDK v6's `sendMessage` accepts `{ text, files }`
   * natively — passing `files` round-trips a `FileUIPart` per attachment
   * to the Bluebird DO, which in turn ships them to Anthropic as image
   * or document content blocks. Keep `files` undefined when none are
   * attached so existing analytics counting "pure text" turns stay
   * accurate.
   */
  onSend: (text: string, files?: File[]) => void;
  onStop: () => void;
  /**
   * Per-file size cap before we refuse a paste/drop/pick. Anthropic's
   * Messages API caps individual image attachments at ~5 MiB and PDF
   * uploads at ~32 MiB; this default (5 MiB) is the safer of the two
   * and matches the screenshot path's default `maxBinaryBytes`. Operators
   * embedding the composer with a different upstream model can override.
   */
  maxAttachmentBytes?: number;
  /**
   * Aggregate cap on the cumulative raw byte size of all PENDING
   * attachments. Defaults to `4 × maxAttachmentBytes` (computed at
   * render time from the resolved per-file cap, NOT a fixed module
   * constant) so operators who lower `maxAttachmentBytes` for a
   * cost-sensitive deployment automatically get a proportionally
   * smaller aggregate budget without also having to remember to
   * pass this option. With both defaults that's ~20 MiB raw /
   * ~27 MiB encoded — comfortably under typical provider
   * request-size ceilings. Without this, a user could attach many
   * individually-valid 5-MiB files and produce a 50+ MiB WebSocket
   * frame that spikes Worker memory + 4xxs at the provider
   * boundary.
   */
  maxAggregateAttachmentBytes?: number;
  /**
   * Maximum number of pending attachments. Anthropic accepts up to
   * 20 image content blocks per message; 10 is the safer default
   * because each attachment also costs vision-token budget.
   */
  maxAttachmentCount?: number;
}

const DEFAULT_MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const DEFAULT_AGGREGATE_MULTIPLIER = 4;
const DEFAULT_MAX_ATTACHMENT_COUNT = 10;

/**
 * Concrete media types accepted by the upstream model (Anthropic
 * Claude) as inline vision content. Mirrors the sandbox-side gate
 * (`@leibniz/env-cloudflare-sandbox/file-tools.ts`'s
 * `VISION_IMAGE_MEDIA_TYPES`) so the user-input path and the
 * tool-result path agree on what counts as multimodal.
 *
 * Kept narrow on purpose — Anthropic's Messages API rejects e.g.
 * `image/svg+xml`, `image/tiff`, `image/bmp`, `image/heic` even though
 * a `image/*` prefix match would admit them. Allowing them through
 * the composer would surface as a 4xx at the provider boundary on
 * send, with no recovery path. Anything outside the supported set is
 * refused at the UI with an explicit error pill — operators wanting
 * to round-trip arbitrary binaries through to the agent should write
 * them to the sandbox workspace via the API surface and read them
 * back through `sandbox_read_file`.
 */
const ACCEPTED_MIME_TYPES = new Set<string>([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
]);

function isAcceptableMime(mime: string): boolean {
  return ACCEPTED_MIME_TYPES.has(mime);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Bottom-of-pane composer. Uncontrolled textarea via ref so per-keystroke
 * cost stays minimal; auto-grows up to 8 lines then scrolls. Enter sends,
 * Shift+Enter inserts a newline. While streaming, the send button swaps
 * for a stop button so the user can cancel the in-flight turn.
 *
 * Multimodal input (images + PDFs) is opt-in via the paperclip button or
 * paste / drop. Attachments are shown as pills above the textarea until
 * sent or cleared. The actual upload to Anthropic happens server-side via
 * AI SDK v6's `FileUIPart` round-trip — the composer just hands raw
 * `File` objects to `useChat.sendMessage({ text, files })` and the SDK
 * does the base64 encoding + `image-data` / `file-data` content-block
 * shaping.
 */
export function Composer({
  disabled,
  isStreaming,
  isStopping,
  isReconnecting,
  onSend,
  onStop,
  maxAttachmentBytes = DEFAULT_MAX_ATTACHMENT_BYTES,
  maxAggregateAttachmentBytes,
  maxAttachmentCount = DEFAULT_MAX_ATTACHMENT_COUNT,
}: ComposerProps) {
  // Derive the aggregate cap from the resolved per-file cap rather
  // than a fixed module constant. If an embedding operator lowers
  // `maxAttachmentBytes` for a cost-sensitive deployment without also
  // setting `maxAggregateAttachmentBytes`, the aggregate budget
  // follows proportionally — preserving the documented 4×-per-file
  // safety relationship instead of leaving the default 20-MiB
  // ceiling that would now be 4–10× the per-file cap.
  const aggregateCap =
    maxAggregateAttachmentBytes ??
    DEFAULT_AGGREGATE_MULTIPLIER * maxAttachmentBytes;
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Track empty-state for button disable without making the textarea
  // controlled (avoid per-keystroke React renders for the input itself).
  const [isEmpty, setEmpty] = useState(true);
  // Each pending attachment is paired with a stable id so React's
  // reconciler keys per-attachment rather than per-position. Without
  // this, removing the first pill would re-key every subsequent pill,
  // re-running their effects (focus rings, hover state) and tripping
  // a known anti-pattern. `crypto.randomUUID` is stable across all
  // modern browsers (and mocked in our test runner) so a polyfill
  // isn't needed.
  const [files, setFiles] = useState<Array<{ id: string; file: File }>>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Concrete `accept` attribute matches `ACCEPTED_MIME_TYPES`. The
  // native picker hides anything outside this list (so the user
  // doesn't even see SVG/TIFF/HEIC files in the picker dialog), and
  // the runtime check in `addFiles` catches the paste / drop paths
  // that bypass `accept` entirely.
  const acceptAttribute = useMemo(
    () => Array.from(ACCEPTED_MIME_TYPES).join(','),
    [],
  );

  const addFiles = useCallback(
    (incoming: FileList | File[]) => {
      // Pure pre-compute against the rendered `files` snapshot, then
      // commit `setFiles` + `setAttachmentError` separately so the
      // setState updaters stay side-effect free. React 19 batches the
      // two calls inside an event handler so this still produces ONE
      // commit. The previous shape called `setAttachmentError` from
      // inside the `setFiles` updater AND minted UUIDs there, which
      // double-fires under StrictMode (the updater is intentionally
      // re-run to surface impurity bugs in development).
      const next: Array<{ id: string; file: File }> = [...files];
      let aggregate = files.reduce((acc, entry) => acc + entry.file.size, 0);
      const errors: string[] = [];
      for (const file of Array.from(incoming)) {
        if (!isAcceptableMime(file.type)) {
          errors.push(
            `${file.name}: ${file.type || 'unknown type'} not supported`,
          );
          continue;
        }
        if (file.size > maxAttachmentBytes) {
          errors.push(
            `${file.name}: ${formatBytes(file.size)} > ${formatBytes(maxAttachmentBytes)} cap`,
          );
          continue;
        }
        if (next.length >= maxAttachmentCount) {
          errors.push(
            `${file.name}: too many attachments (max ${maxAttachmentCount})`,
          );
          continue;
        }
        if (aggregate + file.size > aggregateCap) {
          errors.push(
            `${file.name}: aggregate ${formatBytes(aggregate + file.size)} > ${formatBytes(aggregateCap)} cap`,
          );
          continue;
        }
        next.push({ id: crypto.randomUUID(), file });
        aggregate += file.size;
      }
      // Single-shot commits. If nothing was accepted, only the error
      // pill changes; if everything was accepted, only the file list
      // changes; mixed cases update both.
      if (next.length !== files.length) setFiles(next);
      setAttachmentError(errors.length > 0 ? errors.join(' · ') : null);
    },
    [files, maxAttachmentBytes, aggregateCap, maxAttachmentCount],
  );

  const removeFileById = useCallback((id: string) => {
    setFiles((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const send = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const value = el.value;
    const hasText = value.trim().length > 0;
    const hasFiles = files.length > 0;
    // Block empty sends, but allow files-only (e.g. "what's in this
    // image?" without typed text).
    if (!hasText && !hasFiles) return;
    onSend(
      value,
      hasFiles ? files.map((entry) => entry.file) : undefined,
    );
    el.value = '';
    el.style.height = 'auto';
    setEmpty(true);
    setFiles([]);
    setAttachmentError(null);
  }, [files, onSend]);

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.files;
      if (items && items.length > 0) {
        e.preventDefault();
        addFiles(items);
      }
    },
    [addFiles],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLFormElement>) => {
      const dropped = e.dataTransfer?.files;
      if (dropped && dropped.length > 0) {
        e.preventDefault();
        addFiles(dropped);
      }
    },
    [addFiles],
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLFormElement>) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
    }
  }, []);

  return (
    <form
      className="border-t border-white/8 bg-background/40 px-4 py-3"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
      onDrop={onDrop}
      onDragOver={onDragOver}
    >
      {files.length > 0 && (
        <ul
          className="mx-auto mb-2 flex max-w-3xl flex-wrap gap-2"
          aria-label="Pending attachments"
        >
          {files.map(({ id, file }) => (
            <li
              key={id}
              className="flex items-center gap-2 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs"
            >
              <span className="opacity-90" translate="no">
                {file.name}
              </span>
              <span className="opacity-50">{formatBytes(file.size)}</span>
              <button
                type="button"
                onClick={() => removeFileById(id)}
                className="rounded px-1 text-[11px] opacity-60 hover:bg-white/10 hover:opacity-100"
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {attachmentError && (
        <p
          className="mx-auto mb-1.5 max-w-3xl text-[11px] text-red-400"
          role="alert"
        >
          {attachmentError}
        </p>
      )}
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptAttribute}
          className="hidden"
          onChange={(e) => {
            const picked = e.currentTarget.files;
            if (picked && picked.length > 0) addFiles(picked);
            // Reset so picking the same file twice in a row still fires.
            e.currentTarget.value = '';
          }}
        />
        <Button
          type="button"
          variant="ghost"
          isDisabled={disabled}
          onPress={() => fileInputRef.current?.click()}
          aria-label="Attach images or PDFs"
        >
          📎
        </Button>
        <label htmlFor="composer-input" className="sr-only">
          Message Bluebird
        </label>
        <TextArea
          id="composer-input"
          ref={ref}
          name="message"
          rows={1}
          aria-label="Message Bluebird"
          placeholder="Ask Bluebird anything…"
          autoComplete="off"
          spellCheck={true}
          className="min-h-10 max-h-56 flex-1 resize-none"
          // Auto-grow + state mirror in one onInput so per-keystroke cost
          // is one ref read + one setState only when the empty-state flips.
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 224)}px`;
            const nextEmpty = el.value.trim().length === 0;
            if (nextEmpty !== isEmpty) setEmpty(nextEmpty);
          }}
          onPaste={onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
          disabled={disabled}
        />
        {isStreaming ? (
          <Button
            type="button"
            variant="outline"
            onPress={onStop}
            isDisabled={isStopping}
            aria-label={isStopping ? 'Stopping generation' : 'Stop generation'}
          >
            {isStopping ? 'Stopping…' : 'Stop'}
          </Button>
        ) : (
          <Button
            type="submit"
            variant="primary"
            isDisabled={disabled || (isEmpty && files.length === 0)}
            aria-label="Send message"
          >
            Send
          </Button>
        )}
      </div>
      <p className="mx-auto mt-1.5 max-w-3xl text-[11px] opacity-50">
        {isStopping
          ? 'Stopping… an in-flight tool call may delay confirmation.'
          : isReconnecting
            ? 'Reconnecting… your message will send once the connection returns.'
            : `Enter to send · Shift+Enter for newline · paste / drop / 📎 to attach images & PDFs (up to ${maxAttachmentCount}, ${formatBytes(aggregateCap)} total)`}
      </p>
    </form>
  );
}
